// Diagram chwytu jako SVG, bez zależności.
//
// Układ (siatka strun i progów, znaczniki pustych i tłumionych strun nad
// siodełkiem, numer progu bazowego z lewej, barré jako belka pod kropkami)
// wzorowany na `chordbox.js` z 0xfe/vexchords (MIT). Kod jest napisany od
// zera: vexchords rysuje przez svg.js, a tu wystarcza zwykły znacznik SVG.

import type { PozycjaChwytu } from '../music/chwyty.ts';

const STRUNY = 6;
const PROGI = 4;
const X0 = 22;
const Y0 = 26;
const ROZSTAW_STRUN = 14;
const ROZSTAW_PROGOW = 18;
const SZER = X0 + ROZSTAW_STRUN * (STRUNY - 1);
const WYS = Y0 + ROZSTAW_PROGOW * PROGI;

const x = (struna: number): number => X0 + struna * ROZSTAW_STRUN;
/** Środek pola progu `prog` (1..PROGI), tam siedzi palec. */
const yPalca = (prog: number): number => Y0 + (prog - 0.5) * ROZSTAW_PROGOW;

/** Buduje znacznik `<svg>` jednej pozycji. Kolory idą przez klasy CSS
 * (`.og-dg-*`), żeby diagram brał paletę z motywu, a nie miał jej w sobie. */
export function diagramSvg(pozycja: PozycjaChwytu, etykieta: string): string {
  const czesci: string[] = [];

  for (let p = 0; p <= PROGI; p++) {
    const y = Y0 + p * ROZSTAW_PROGOW;
    czesci.push(`<line class="og-dg-prog" x1="${X0}" y1="${y}" x2="${SZER}" y2="${y}"/>`);
  }
  for (let s = 0; s < STRUNY; s++) {
    czesci.push(`<line class="og-dg-struna" x1="${x(s)}" y1="${Y0}" x2="${x(s)}" y2="${WYS}"/>`);
  }
  if (pozycja.progBazowy <= 1) {
    czesci.push(`<rect class="og-dg-siodelko" x="${X0 - 1}" y="${Y0 - 4}" width="${SZER - X0 + 2}" height="4"/>`);
  } else {
    czesci.push(`<text class="og-dg-nrprogu" x="${X0 - 7}" y="${yPalca(1) + 4}" text-anchor="end">${pozycja.progBazowy}</text>`);
  }

  // Barré: belka od pierwszej do ostatniej struny przyciśniętej na tym progu.
  for (const prog of pozycja.barre) {
    const struny = pozycja.progi.map((f, i) => (f === prog ? i : -1)).filter((i) => i >= 0);
    if (struny.length < 2) continue;
    const od = x(Math.min(...struny));
    const doX = x(Math.max(...struny));
    czesci.push(`<rect class="og-dg-barre" x="${od - 5}" y="${yPalca(prog) - 5}" width="${doX - od + 10}" height="10" rx="5"/>`);
  }

  // Palec, który kładzie barré na danym progu: struny tym palcem na tym
  // progu przykrywa belka, więc nie dostają osobnej kropki.
  const palecBarre = new Map<number, number>();
  for (const prog of pozycja.barre) {
    const i = pozycja.progi.indexOf(prog);
    if (i >= 0) palecBarre.set(prog, pozycja.palce[i] ?? 0);
  }

  pozycja.progi.forEach((prog, s) => {
    if (prog < 0) {
      const cx = x(s);
      const cy = Y0 - 11;
      czesci.push(`<path class="og-dg-tlum" d="M${cx - 3.5} ${cy - 3.5}L${cx + 3.5} ${cy + 3.5}M${cx + 3.5} ${cy - 3.5}L${cx - 3.5} ${cy + 3.5}"/>`);
    } else if (prog === 0) {
      czesci.push(`<circle class="og-dg-pusta" cx="${x(s)}" cy="${Y0 - 11}" r="3.8"/>`);
    } else if (palecBarre.get(prog) !== pozycja.palce[s]) {
      czesci.push(`<circle class="og-dg-palec" cx="${x(s)}" cy="${yPalca(prog)}" r="5.4"/>`);
    }
  });

  const tytul = etykieta.replace(/[<&"]/g, '');
  return `<svg class="og-dg" viewBox="0 0 ${SZER + 10} ${WYS + 6}" role="img" aria-label="chwyt ${tytul}">${czesci.join('')}</svg>`;
}
